import { DEFAULT_APPEARANCE } from '@/theme/engine.mjs';
import { canServerPrint, printerMode } from "@/lib/printer-mode";
import { variantSummary } from "@/lib/variant-quantities.mjs";
import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { rupiah } from "@/lib/format";
import { api, apiError } from "@/lib/pos-api";
import { printViaBluetooth } from "@/lib/btprint";
import { printReceipt, printReceiptAutomatically } from "@/lib/printReceipt";
import { cacheSettings, readSettings, readCachedProducts, readCachedCategories } from "@/lib/offline";
import { canPrintNonTobacco, catalogReceiptItems, nonTobaccoReceipt, receiptGroups } from '@/lib/receipt-groups.mjs';
import { toast } from "sonner";
import { getActiveStore } from '@/lib/scope';
import { ReceiptPreviewFrame } from '@/components/ReceiptTemplatePreview';
import { receiptPaperWidth } from '@/lib/nota-layout';
import { Printer, Check, X, Bluetooth, Wifi, Loader2 } from "lucide-react";

const methodLabel = { tunai: "Tunai", transfer: "Transfer", qris: "QRIS", kartu_debit: "Kartu Debit", kartu_kredit: "Kartu Kredit" };
const mLabel = (m) => methodLabel[m] || (m ? m.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : m);
// Screen-only chrome. Never included in the HTML sent to the printer.
const panelColors = { backgroundColor: '#dbeafe', color: '#000' };

export default function ReceiptModal({ tx, cashier, onClose, isReprint = false, autoPrint = true }) {
  const [settings, setSettings] = useState(null);
  const [settingsFor, setSettingsFor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [classified, setClassified] = useState(null);
  const printedFor = useRef(null);
  const prepared = classified?.source === tx ? classified.value : tx;
  const receiptTx = prepared && isReprint ? { ...prepared, is_reprint: true } : prepared;
  const ethernetPayload = { transaction_id: tx?.id, ...(isReprint ? { reprint: true } : {}) };

  useEffect(() => {
    if (!tx) return;
    let current = true;
    const store = tx.store_id || getActiveStore();
    const classify = async () => {
      if (tx.items.every(item => typeof item.receipt_tobacco === 'boolean')) return tx;
      let items = tx.items;
      try {
        const ids = [...new Set(items.filter(item => typeof item.receipt_tobacco !== 'boolean').map(item => item.product_id).filter(Boolean))];
        if (ids.length) {
          const { data } = await api.post('/print/item-categories', { product_ids: ids }, { headers: { 'X-Store-Id': store } });
          items = items.map(item => typeof item.receipt_tobacco === 'boolean' ? item : { ...item, receipt_tobacco: data[item.product_id] });
        }
      } catch {
        if (store === getActiveStore()) items = catalogReceiptItems(items, readCachedProducts?.() || [], readCachedCategories?.() || []);
      }
      return { ...tx, items };
    };
    Promise.all([api.get("/settings", { headers: { 'X-Store-Id': store } }), classify()]).then(([r, value]) => {
      if (!current) return;
      setClassified({ source: tx, value });
      setSettings({ ...r.data, ...tx.receipt_identity }); setSettingsFor(tx);
      if (store === getActiveStore()) cacheSettings(r.data);
    }).catch(() => {
      if (!current) return;
      const cached = readSettings();
      if (cached.store_id !== store) {
        toast.error('Pengaturan cabang asal belum tersedia. Hubungkan internet untuk mencetak.');
        return;
      }
      setSettings({ ...cached, ...tx.receipt_identity }); setSettingsFor(tx);
      setClassified({ source: tx, value: { ...tx, items: catalogReceiptItems(tx.items, readCachedProducts?.() || [], readCachedCategories?.() || []) } });
    });
    return () => { current = false; };
  }, [tx]);

  // Claim each job synchronously so settings updates/StrictMode cannot double-print.
  useEffect(() => {
    if (!autoPrint || !tx || settings === null || settingsFor !== tx) return;
    if (settings.auto_print === false) return;
    const printKey = `${tx.id || tx.invoice_no}:${isReprint ? 'reprint' : 'sale'}`;
    if (printedFor.current === printKey) return;
    printedFor.current = printKey;
    const s2 = settings || {};
    const mode = printerMode(s2);
    if (mode === "ethernet" && s2.eth_ip) {
      const request = tx.invoice_no?.includes("(REVISI)") || tx.offline
        ? api.post("/print/draft", receiptTx)
        : api.post("/print/ethernet", ethernetPayload);
      request
        .then(() => toast.success("Struk tercetak"))
        .catch((e) => toast.error("Auto-cetak gagal: " + apiError(e)));
    } else if (mode === "bluetooth") {
      printViaBluetooth(receiptTx, s2, cashier).then(() => toast.success("Struk tercetak")).catch(() => toast.error("Cetak otomatis Bluetooth gagal. Tekan tombol cetak untuk mencoba lagi."));
    } else if (mode === "browser") {
      const failed = () => toast.error("Cetak otomatis gagal. Transaksi sudah tersimpan; tekan Cetak Struk untuk mencoba lagi.");
      if (!printReceiptAutomatically(receiptTx, s2, cashier, failed)) failed();
    }
    // eslint-disable-next-line
  }, [tx, settings, settingsFor, isReprint, autoPrint]);

  if (!tx) return null;
  const s = settings || {};
  const displayGroups = receiptGroups(receiptTx.items);
  const date = new Date(tx.created_at);
  const dateStr = date.toLocaleString("id-ID", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });

  const printBt = async (target = receiptTx) => {
    setBusy(true);
    try {
      await printViaBluetooth(target, s, cashier);
      toast.success("Dikirim ke printer Bluetooth");
    } catch (e) {
      toast.error(e.message || "Gagal cetak Bluetooth");
    } finally {
      setBusy(false);
    }
  };

  const printEth = async (target = receiptTx) => {
    if (!canServerPrint(s)) {
      if (!printReceipt(target, s, cashier)) toast.error("Izinkan popup browser untuk mencetak struk");
      return;
    }
    setBusy(true);
    try {
      if (tx.invoice_no?.includes("(REVISI)") || tx.offline) await api.post("/print/draft", target);
      else await api.post("/print/ethernet", { ...ethernetPayload, ...(target.receipt_scope === 'non_tobacco' ? { receipt_scope: 'non_tobacco' } : {}) });
      toast.success("Dikirim ke printer Ethernet");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const printConfigured = (nonTobacco = false) => {
    if (busy || settingsFor !== tx || settings === null) return;
    if (nonTobacco && !canPrintNonTobacco(receiptTx.items)) return;
    const target = nonTobacco ? nonTobaccoReceipt(receiptTx) : receiptTx;
    const mode = printerMode(s);
    if (mode === "ethernet") {
      if (!s.eth_ip) { toast.error("Isi alamat IP printer di Pengaturan Printer terlebih dahulu"); return; }
      return printEth(target);
    }
    if (mode === "bluetooth") return printBt(target);
    if (!printReceipt(target, s, cashier)) toast.error("Izinkan popup browser untuk mencetak struk");
  };

  return (
    <Dialog open={!!tx} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="thermal-receipt-modal"
        className="flex flex-col gap-0 border-blue-300 max-w-none max-h-[calc(100dvh-16px)] p-0 overflow-hidden rounded-lg"
        style={{ ...panelColors, width: `calc(${receiptPaperWidth(s)}mm + 2px)`, maxWidth: 'calc(100vw - 16px)' }}
      >
        <DialogTitle className="sr-only">Struk Pembayaran {tx.invoice_no}</DialogTitle>
        <DialogDescription className="sr-only">Detail struk transaksi dan tombol cetak</DialogDescription>
        <div data-testid="receipt-screen-header" className="p-3 pr-10 flex shrink-0 items-center gap-2 border-b border-blue-300" style={panelColors}>
          <div className="h-8 w-8 shrink-0 rounded-full bg-blue-300 flex items-center justify-center">
            <Check className="h-5 w-5 text-black" />
          </div>
          <div>
            <div className="text-sm font-bold break-words">{isReprint ? 'Cetak Ulang Struk' : 'Pembayaran Berhasil'}</div>
          </div>
        </div>

        {/* Printable thermal area */}
        <div data-testid="receipt-screen-preview" className="min-h-0 flex-auto overflow-auto bg-white [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none' }}>
          {printerMode(s) === 'browser' ? <ReceiptPreviewFrame tx={receiptTx} settings={s} cashier={cashier} /> : <div id="thermal-print" className="bg-white text-black p-4 font-mono text-[12px] leading-tight">
            <div className="text-center">
              {isReprint && <div data-testid="receipt-reprint-label">(REPRINT)</div>}
              <div className="font-bold text-base">{s.store_name || DEFAULT_APPEARANCE.branding.name}</div>
              {s.address ? <div className="text-[10px]">{s.address}</div> : null}
              {s.phone ? <div className="text-[10px]">Telp {s.phone}</div> : null}
            </div>
            <div className="border-t border-dashed border-black my-2" />
            <div className="flex justify-between text-[11px]">
              <span>No: {tx.invoice_no}</span>
            </div>
            <div className="flex justify-between text-[11px]">
              <span>{dateStr}</span>
            </div>
            <div className="flex justify-between text-[11px]">
              <span>Kasir: {cashier}</span>
            </div>
            <div className="border-t border-dashed border-black my-2" />
            {displayGroups.items.map((it, i) => (
              <div key={i} className="mb-1">
                {i === displayGroups.separator && <div className="border-t border-dashed border-black my-2" />}
                <div>{it.name}</div>
                {variantSummary(it) && <div data-testid={`receipt-variants-${i}`}>{variantSummary(it)}</div>}
                <div className="flex justify-between">
                  <span>{it.qty} x {rupiah(it.price)} <span className="uppercase">({it.price_level.slice(0,3)})</span></span>
                  <span>{rupiah(it.qty * it.price)}</span>
                </div>
                {it.discount > 0 && (
                  <div className="flex justify-between"><span>&nbsp;&nbsp;Diskon item</span><span>-{rupiah(it.discount)}</span></div>
                )}
                {it.note ? <div className="italic">&nbsp;&nbsp;* {it.note}</div> : null}
              </div>
            ))}
            <div className="border-t border-dashed border-black my-2" />
            <Row label="Jumlah Produk" value={tx.items.length} />
            <Row label="Subtotal" value={rupiah(tx.subtotal)} />
            {tx.discount > 0 && <Row label="Diskon" value={"-" + rupiah(tx.discount)} />}
            {(tx.previous_debts || []).map(debt => <Row key={debt.invoice_id} label={`Piutang sebelumnya (${debt.invoice_no.slice(-4)})`} value={rupiah(debt.amount)} />)}
            <div className="flex justify-between font-bold text-sm mt-1">
              <span>TOTAL</span>
              <span>{rupiah(tx.total + (tx.settlement_total || 0))}</span>
            </div>
            {tx.fee > 0 && (
              <>
                <Row label="Biaya" value={"+" + rupiah(tx.fee)} />
                <div className="flex justify-between font-bold text-sm">
                  <span>TOTAL BAYAR</span>
                  <span>{rupiah(tx.grand_total || (tx.total + tx.fee))}</span>
                </div>
              </>
            )}
            {tx.return_credit_applied > 0 && <Row label="Saldo Retur" value={"-" + rupiah(tx.return_credit_applied)} />}
            <div className="border-t border-dashed border-black my-2" />
            {(tx.checkout_payments || tx.payments).map((p, i) => (
              <Row key={i} label={mLabel(p.method)} value={rupiah(p.amount)} />
            ))}
            <Row label="Bayar" value={rupiah(tx.paid)} />
            <Row label="Kembali" value={rupiah(tx.change)} />
            {tx.customer_name ? <Row label="Pelanggan" value={tx.customer_name} /> : null}
            {tx.credit > 0 ? (
              <div className="flex justify-between font-bold text-[12px] mt-1">
                <span>SISA PIUTANG</span>
                <span>{rupiah(tx.credit)}</span>
              </div>
            ) : null}
            <div className="border-t border-dashed border-black my-2" />
            {tx.note && <div data-testid="receipt-note" className="mb-2 whitespace-pre-wrap break-words">Catatan: {tx.note}</div>}
            <div className="text-center text-[11px] whitespace-pre-line">
              {s.footer || "*** TERIMA KASIH ***"}
            </div>
          </div>}
        </div>

        <div data-testid="receipt-screen-actions" className="p-3 space-y-2 shrink-0 max-h-[55dvh] overflow-auto border-t border-blue-300" style={panelColors}>
          <div data-testid="receipt-change-summary" role="status" className="text-center pb-1" style={panelColors}>
            <div className="text-sm font-semibold">KEMBALIAN</div>
            <div data-testid="receipt-change-amount" className="text-3xl leading-tight font-bold tabular-nums" style={{ overflowWrap: 'anywhere' }}>{rupiah(tx.change)}</div>
          </div>
            <Button
              data-testid="print-receipt-btn"
              onClick={() => printConfigured()}
              disabled={busy || settingsFor !== tx || settings === null}
              className="w-full h-11 bg-blue-300 hover:bg-blue-400 text-black hover:text-black font-bold gap-2"
            >
              <Printer className="h-4 w-4" /> Cetak Resi
            </Button>
          <div className={`grid gap-2 ${receiptPaperWidth(s) >= 70 ? 'grid-cols-2' : 'grid-cols-1'}`}>
            <Button
              data-testid="print-bt-btn"
              onClick={() => printBt()}
              disabled={busy || settingsFor !== tx}
              variant="outline"
              className="h-10 px-2 text-xs border-blue-300 bg-blue-50 hover:bg-blue-200 text-black hover:text-black gap-1"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bluetooth className="h-4 w-4" />} Bluetooth
            </Button>
          <Button data-testid="print-non-tobacco-btn" variant="outline" className="w-full h-10 px-2 text-xs gap-1 border-blue-300 bg-blue-50 hover:bg-blue-200 text-black hover:text-black"
            disabled={busy || settingsFor !== tx || !canPrintNonTobacco(receiptTx.items)} onClick={() => printConfigured(true)}>
            <Printer className="h-4 w-4" /> Cetak Nonrokok
          </Button>
          </div>
          {receiptTx.items.some(item => typeof item.receipt_tobacco !== 'boolean') && <p className="text-xs text-black">Kategori belum lengkap. Cetak nonrokok tersedia setelah kategori barang berhasil dimuat.</p>}
          {canServerPrint(s) ? (
            <Button
              data-testid="print-eth-btn"
              onClick={() => printEth()}
              disabled={busy || settingsFor !== tx}
              variant="outline"
              className="w-full h-auto min-h-11 whitespace-normal border-blue-300 bg-blue-50 hover:bg-blue-200 text-black hover:text-black gap-2"
            >
              <Wifi className="h-4 w-4 shrink-0" /> Cetak via Server ({s.eth_ip})
            </Button>
          ) : null}
          <Button
            data-testid="close-receipt-btn"
            variant="ghost"
            onClick={onClose}
            className="w-full h-8 text-black hover:text-black hover:bg-blue-200 gap-2"
          >
            <X className="h-4 w-4" /> Tutup
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-[11px]">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
